import { baseApi } from "@/shared/api";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type { SigningActionResponse } from "@/shared/types";
import { decrementPendingActionsCount } from "@/features/auth";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import type { SignDocumentDetails } from "@/features/self/pending/types/pending.types";
import type {
  CandidateJobApplicationDetail,
  JobApplicationReviewRequest,
  JobApplicationReviewResponse,
} from "@/features/hr/contracts/types";

export const jobApplicationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getJobApplication: builder.query<CandidateJobApplicationDetail, number>({
      query: (id) => ({
        url: `/api/hr/candidates/${id}/job-application/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [
        { type: "Applications" as const, id },
        { type: "Contracts" as const, id: "LIST" },
      ],
    }),
    previewJobApplication: builder.mutation<FileResponse, number>({
      query: (candidateId) => ({
        url: `/api/hr/candidates/${candidateId}/preview-job-application/`,
        method: "GET",
        credentials: "include",
        responseHandler: fileResponseHandler,
      }),
    }),
    reviewJobApplication: builder.mutation<
      JobApplicationReviewResponse,
      { candidateId: number; body: JobApplicationReviewRequest }
    >({
      query: ({ candidateId, body }) => ({
        url: `/api/hr/candidates/${candidateId}/review-job-application/`,
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { candidateId }) => [
        { type: "Contracts" as const, id: "LIST" },
        { type: "Applications" as const, id: candidateId },
      ],
    }),
    signJobApplication: builder.mutation<
      SigningActionResponse,
      { candidateId: number; cms_signature: string }
    >({
      query: ({ candidateId, cms_signature }) => ({
        url: `/api/hr/candidates/${candidateId}/sign-job-application/`,
        method: "POST",
        body: { cms_signature },
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { candidateId }) => [
        { type: "Contracts" as const, id: "LIST" },
        { type: "Applications" as const, id: candidateId },
        "PendingActions",
      ],
      async onQueryStarted({ candidateId }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) =>
                item.action_required === "sign_document" &&
                "document_id" in item.details &&
                (item.details as SignDocumentDetails).source_type === "job_application" &&
                (item.details as SignDocumentDetails).candidate_id === candidateId,
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(
            jobApplicationsApi.util.updateQueryData("getJobApplication", candidateId, (draft) => {
              if (draft.job_application) draft.job_application.signing_status = 1;
            }),
          );
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),
  }),
});

export const {
  useGetJobApplicationQuery,
  useLazyGetJobApplicationQuery,
  usePreviewJobApplicationMutation,
  useReviewJobApplicationMutation,
  useSignJobApplicationMutation,
} = jobApplicationsApi;
